<template>
  <div v-if="allowUpload || allowDelete">
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      style="display: none"
      @change="onFileSelected"
    />

    <b-button
      class="mt-3"
      variant="secondary"
      size="sm"
      :disabled="disabled"
      @click="selectFile()"
      v-if="allowUpload"
    >
      <font-awesome-icon :icon="['fas', 'folder-open']" />
      {{ $t('Webportal.SelectFile') }}
    </b-button>

    <b-button
      class="mt-3"
      variant="secondary"
      size="sm"
      :disabled="disabled"
      @click="deleteFile()"
      v-if="allowDelete"
    >
      <font-awesome-icon :icon="['fas', 'trash-alt']" />
      {{ $t('Webportal.Delete') }}
    </b-button>
  </div>
</template>

<script>
import AuthenticationService from '@/services/AuthenticationService'
import axios from 'axios';
import { useToast } from 'bootstrap-vue-next';

export default {
  name: "Uploader",

  props: {
    uploadUrl: { type: String, default: '/' },
    accept: { type: String, default: 'image/*' },
    allowedFileExtensions: { type: Array, default: () => ['.jpg', '.jpeg', '.gif', '.png'] },
    allowDelete: { type: Boolean, default: true },
    allowUpload: { type: Boolean, default: true },
    valueChanged: { type: Function, default: () => {} },
    disabled: { type: Boolean, default: false },
    directMode: { type: Boolean, default: true }
  },

  setup () {
    const { show } = useToast();
    return { showToast: show };
  },

  data() {
    return {
      selectedFile: null,
      uploadFinished: () => {},
      uploadFailed: () => {}
    };
  },

  computed: {
    authorizationHeader() {
      return AuthenticationService.authorizationHeader;
    }
  },

  methods: {
    selectFile() {
      this.$refs.fileInput.click();
    },

    onFileSelected(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0];

      // Validiere Dateiendung
      if (this.allowedFileExtensions.length > 0) {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!this.allowedFileExtensions.includes(ext)) {
          this.$refs.fileInput.value = null;
          return;
        }
      }

      this.selectedFile = file;

      // Trigger valueChanged callback
      if (this.valueChanged instanceof Function) {
        this.valueChanged({ value: [file] });
      }

      this.$refs.fileInput.value = null;
      if (this.directMode) {
        this.uploadFile();
      }
    },

    deleteFile() {
      this.selectedFile = null;
      this.$emit('input', null);

      // direct Mode: triggers immediate upload to notify backend about deletion, else uploadFile must be called
      if (this.directMode) {
        if (!this.uploadUrl) {
          return Promise.reject('No upload URL specified');
        }

        return axios.post(this.uploadUrl, null, {
          headers: this.authorizationHeader
        });
      }
    },

    reset() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },

    uploadFile() {
      if (!this.uploadUrl) {
        return Promise.reject('No upload URL specified');
      }

      const formData = new FormData();
      formData.append('files[]', this.selectedFile);
      const bodyText = this.selectedFile
        ? `${this.selectedFile.name} uploaded...`
        : `Old file removed...`;

      return axios.post(this.uploadUrl, this.selectedFile ? formData : null, {
        headers: {
          ...this.authorizationHeader
          // 'Content-Type': 'multipart/form-data' wird automatisch von axios gesetzt
        }
      })
      .then(response => {

        this.showToast({
          props: {
            title: "Upload Successful",
            body: bodyText,
            variant: "success",
            autoHide: true,
            delay: 3000,
          }
        });

        this.$emit('uploaded', response);
        if (this.uploadFinished instanceof Function) {
          this.uploadFinished(response);
        }
        return response;
      })
      .catch(error => {
        this.$emit('upload-error', error);
        if (this.uploadFailed instanceof Function) {
          this.uploadFailed(error);
        }
        throw error;
      });
    },

    onUploaded(e) {
      if (this.uploadFinished instanceof Function)
        this.uploadFinished(e);
    },

    onUploadFailed(e) {
      if (this.uploadFailed instanceof Function)
        this.uploadFailed(e);
    }
  }
}
</script>
